import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { branchService } from '../services';
import { Branch, BusinessCategory, City } from '../types';
import businessCategoryService from '../services/businessCategories';
import locationService from '../services/locations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  ArrowLeft,
  Save,
  MapPin,
  Edit,
  X,
  Building2,
  DollarSign,
  Users,
  Mail,
  Phone as PhoneIcon,
  Globe,
  Image as ImageIcon,
} from 'lucide-react';
import { useForm } from 'react-hook-form';

interface BranchFormData {
  name: string;
  description?: string;
  address: string;
  businessCategoryIds: number[];
  commissionRate: number | string | '';
  baseCommission: number | string | '';
  minPeople: number | string | '';
  maxPeople: number | string | '';
  latitude: number | string | '';
  longitude: number | string | '';
  phone: string;
  email: string;
  website: string;
  cityId: number | string | '';
}

// Helper function to convert string to number or null
const toNumberOrNull = (value: number | string | '' | undefined | null) => {
  if (value === '' || value === undefined || value === null) return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

// Helper function to convert string to number or undefined (for cityId)
const toNumberOrUndefined = (value: number | string | '' | undefined | null) => {
  if (value === '' || value === undefined || value === null) return undefined;
  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

export const BranchDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [branch, setBranch] = useState<Branch | null>(null);
  const [categories, setCategories] = useState<BusinessCategory[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [branchImages, setBranchImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BranchFormData>();

  useEffect(() => {
    if (id) {
      loadBranch();
      loadCategories();
      loadCities();
    }
  }, [id]);

  const loadCities = async () => {
    try {
      const data = await locationService.getAllCities();
      setCities(data);
    } catch (err: any) {
      console.error('Failed to load cities:', err);
    }
  };

  const loadBranch = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await branchService.getBranchById(id);
      setBranch(data);
      const categoryIds = data.businessCategories?.map((cat) => cat.id) || [];
      setSelectedCategoryIds(categoryIds);
      setBranchImages(data.imageUrls || []);
      reset({
        name: data.name,
        description: data.description ?? '',
        address: data.address,
        businessCategoryIds: categoryIds,
        commissionRate: data.commissionRate ?? '',
        baseCommission: data.baseCommission ?? '',
        minPeople: data.minPeople ?? '',
        maxPeople: data.maxPeople ?? '',
        latitude: data.latitude ?? '',
        longitude: data.longitude ?? '',
        phone: data.phone ?? '',
        email: data.email ?? '',
        website: data.website ?? '',
        cityId: data.city?.id ?? '',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load branch');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await businessCategoryService.getAllCategoriesAdmin(true);
      setCategories(data);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
    }
  };

  const onSubmit = async (data: BranchFormData) => {
    if (!id) return;

    try {
      setError(null);
      await branchService.updateBranch(id, {
        name: data.name,
        description: data.description || undefined,
        address: data.address,
        businessCategoryIds: selectedCategoryIds,
        commissionRate: toNumberOrNull(data.commissionRate),
        baseCommission: toNumberOrNull(data.baseCommission),
        minPeople: toNumberOrNull(data.minPeople),
        maxPeople: toNumberOrNull(data.maxPeople),
        latitude: toNumberOrNull(data.latitude),
        longitude: toNumberOrNull(data.longitude),
        phone: data.phone || null,
        email: data.email || null,
        website: data.website || null,
        cityId: toNumberOrUndefined(data.cityId),
        images: branchImages,
      });
      await loadBranch();
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update branch');
    }
  };

  if (loading && !branch) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !branch) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Branch not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/branches')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">{branch.name}</h1>
              <Badge variant={branch.isMainBranch ? 'default' : 'secondary'}>
                {branch.isMainBranch ? 'Main Branch' : 'Branch'}
              </Badge>
            </div>
            <p className="text-muted-foreground">Branch Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  reset();
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSubmit(onSubmit)}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Branch Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Branch details and description</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <form className="space-y-4">
                <div>
                  <Label htmlFor="name">Branch Name *</Label>
                  <Input id="name" {...register('name', { required: true })} />
                  {errors.name && <p className="text-sm text-destructive">Name is required</p>}
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input id="description" {...register('description')} />
                </div>
                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Input id="address" {...register('address', { required: true })} />
                  {errors.address && (
                    <p className="text-sm text-destructive">Address is required</p>
                  )}
                </div>
              </form>
            ) : (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{branch.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="font-medium">{branch.description || '-'}</p>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium">{branch.address}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={branch.active ? 'default' : 'secondary'}>
                    {branch.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Business Categories</CardTitle>
            <CardDescription>Services offered by this branch</CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div className="space-y-2">
                <Label>Select Categories</Label>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border rounded-md p-3">
                  {categories.map((category) => (
                    <label
                      key={category.id}
                      className="flex items-center gap-2 p-2 rounded hover:bg-accent cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategoryIds.includes(category.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCategoryIds([...selectedCategoryIds, category.id]);
                          } else {
                            setSelectedCategoryIds(
                              selectedCategoryIds.filter((id) => id !== category.id)
                            );
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{category.name}</span>
                    </label>
                  ))}
                </div>
                {selectedCategoryIds.length === 0 && (
                  <p className="text-sm text-destructive">At least one category is required</p>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {branch.businessCategories && branch.businessCategories.length > 0 ? (
                  branch.businessCategories.map((category) => (
                    <Badge key={category.id} variant="outline">
                      {category.name}
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No categories assigned</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Commission Settings</CardTitle>
            <CardDescription>Commission rates and base fees (optional)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <form className="space-y-4">
                <div>
                  <Label htmlFor="commissionRate">Commission Rate (%) - Optional</Label>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="commissionRate"
                      type="text"
                      placeholder="e.g., 15.5 (leave empty for none)"
                      {...register('commissionRate')}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="baseCommission">Base Commission - Optional</Label>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="baseCommission"
                      type="text"
                      placeholder="e.g., 100 (leave empty for none)"
                      {...register('baseCommission')}
                    />
                  </div>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Commission Rate</p>
                    <p className="font-medium">
                      {branch.commissionRate ? `${branch.commissionRate}%` : '-'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Base Commission</p>
                    <p className="font-medium">
                      {branch.baseCommission ? `$${branch.baseCommission}` : '-'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Capacity Settings</CardTitle>
            <CardDescription>Minimum and maximum people (optional)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <form className="space-y-4">
                <div>
                  <Label htmlFor="minPeople">Minimum People - Optional</Label>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="minPeople"
                      type="text"
                      placeholder="e.g., 10 (leave empty for none)"
                      {...register('minPeople')}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="maxPeople">Maximum People - Optional</Label>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="maxPeople"
                      type="text"
                      placeholder="e.g., 100 (leave empty for none)"
                      {...register('maxPeople')}
                    />
                  </div>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Minimum People</p>
                    <p className="font-medium">{branch.minPeople ?? '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Maximum People</p>
                    <p className="font-medium">{branch.maxPeople ?? '-'}</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Location Coordinates</CardTitle>
            <CardDescription>GPS coordinates for the branch (optional)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <form className="space-y-4">
                <div>
                  <Label htmlFor="latitude">Latitude - Optional</Label>
                  <Input
                    id="latitude"
                    type="text"
                    placeholder="e.g., 41.0082 (leave empty for none)"
                    {...register('latitude')}
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude - Optional</Label>
                  <Input
                    id="longitude"
                    type="text"
                    placeholder="e.g., 28.9784 (leave empty for none)"
                    {...register('longitude')}
                  />
                </div>
              </form>
            ) : (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">Latitude</p>
                  <p className="font-medium">
                    {branch.latitude ? branch.latitude.toFixed(6) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Longitude</p>
                  <p className="font-medium">
                    {branch.longitude ? branch.longitude.toFixed(6) : '-'}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
            <CardDescription>Contact details for the branch (optional)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <form className="space-y-4">
                <div>
                  <Label htmlFor="phone">Phone - Optional</Label>
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="text"
                      placeholder="e.g., +90 555 123 4567"
                      {...register('phone')}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email - Optional</Label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="e.g., branch@example.com"
                      {...register('email')}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="website">Website - Optional</Label>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="website"
                      type="url"
                      placeholder="e.g., https://example.com"
                      {...register('website')}
                    />
                  </div>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{branch.phone ?? '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{branch.email ?? '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Website</p>
                    {branch.website ? (
                      <a
                        href={branch.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {branch.website}
                      </a>
                    ) : (
                      <p className="font-medium">-</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>City</CardTitle>
            <CardDescription>Branch location city (optional)</CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div>
                <Label htmlFor="cityId">City - Optional</Label>
                <select
                  id="cityId"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  {...register('cityId')}
                >
                  <option value="">No city</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground">City</p>
                <p className="font-medium">{branch.city?.name ?? '-'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Branch Images</CardTitle>
            <CardDescription>Manage branch images (S3 keys)</CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div>
                <Label htmlFor="images">
                  <ImageIcon className="inline h-4 w-4 mr-2" />
                  Images (S3 keys)
                </Label>
                {branchImages.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-muted-foreground">Current images (S3 keys):</p>
                    <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-1">
                      {branchImages.map((imageKey, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded"
                        >
                          <span className="text-xs font-mono truncate flex-1">{imageKey}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setBranchImages(branchImages.filter((_, i) => i !== index))
                            }
                            className="ml-2 text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mt-2">
                    No images uploaded. Use the file upload API to add images.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground">Images</p>
                {branch.imageUrls && branch.imageUrls.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {branch.imageUrls.map((imageKey, index) => (
                      <div key={index} className="p-2 bg-gray-50 rounded">
                        <span className="text-xs font-mono">{imageKey}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="font-medium">No images</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BranchDetailPage;
